import { z } from "zod";
import { displayUrl, normalizeExampleImage, type ExampleImageAdUnit } from "./adImage";

const fraction = z.number().finite().min(0).max(1);
export const photoSlotSchema = z.object({
  x: fraction,
  y: fraction,
  w: fraction.positive(),
  h: fraction.positive(),
  radius: fraction.default(0),
  inset: fraction.default(0),
}).strict().superRefine((slot, ctx) => {
  if (slot.w * slot.h === 0 || slot.x + slot.w === slot.x || slot.y + slot.h === slot.y)
    ctx.addIssue({ code: "custom", message: "Rectangle dimensions are too small to use." });
  if (slot.x + slot.w > 1 || slot.y + slot.h > 1)
    ctx.addIssue({ code: "custom", message: "The whole rectangle must fit inside the template image." });
  // Conservative mask limits remain usable regardless of template aspect ratio.
  const half = Math.min(slot.w, slot.h) / 2;
  if (slot.inset >= half)
    ctx.addIssue({ code: "custom", path: ["inset"], message: "Inset must leave a positive photo area." });
  if (slot.radius > half - slot.inset)
    ctx.addIssue({ code: "custom", path: ["radius"], message: "Radius must fit inside the inset rectangle." });
});
export type PhotoSlot = z.infer<typeof photoSlotSchema>;

// Overlap is intersection area / smaller rectangle area. Touching is zero;
// exactly 50 percent is allowed. Order is the future fill order.
export const photoSlotsSchema = z.object({
  version: z.literal(1),
  slots: z.array(photoSlotSchema).max(16, "At most 16 photo slots are allowed."),
}).strict().superRefine(({ slots }, ctx) => {
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      const a = slots[i], b = slots[j];
      const area = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x))
        * Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
      if (area / Math.min(a.w * a.h, b.w * b.h) > 0.5 + Number.EPSILON * 4)
        ctx.addIssue({ code: "custom", path: ["slots", j], message: `Slots ${i + 1} and ${j + 1} overlap by more than 50 percent of the smaller rectangle.` });
    }
  }
});
export type PhotoSlots = z.infer<typeof photoSlotsSchema>;

// Same precedence as the existing Ads Express source resolver. Pinned by parity tests.
export function resolvePhotoSlotSource(unit: ExampleImageAdUnit): string {
  if (unit.isMeme === true) return unit.memeReferenceImageUrl?.trim() || "";
  for (const url of [unit.adgenExampleImageUrl, unit.exampleImageUrl])
    if (typeof url === "string" && url.trim()) return url.trim();
  if (Array.isArray(unit.exampleImages))
    for (const entry of unit.exampleImages) {
      const url = displayUrl(normalizeExampleImage(entry));
      if (url?.trim()) return url.trim();
    }
  return "";
}

export function photoSlotSourceChanged(before: ExampleImageAdUnit, patch: Partial<ExampleImageAdUnit>): boolean {
  return resolvePhotoSlotSource(before) !== resolvePhotoSlotSource({ ...before, ...patch });
}
